/* eslint-disable camelcase */
import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { debounce } from "@utils/lodash";
import { Button, ControlLabel, FormGroup } from "react-bootstrap";
import MaskedInput from "react-text-mask";
import utils from "@utils";
import { INOVICE_STATUSES_SELECT } from "@constants";
import { PaymentService } from "@services";
import SelectBox from "./SelectBox";
import { ReactSelect, AsyncSelect, SearchOfficerModal } from "../../../../components";

const manualInvoiceReportTypes = ["manual_invoice", "manual_aging", "applied_payments", "deposit"];
const manualInvoiceReportFilters = ["manual_invoice", "applied_payments"];
const dateMask = [/[0-1]/, /[0-9]/, "/", /[0-3]/, /[0-9]/, "/", /2/, /0/, /[2-9]/, /[0-9]/];
const maxStartDate = utils.formatDate(undefined, undefined, undefined, "noslash");
class SideBar extends Component {
	constructor(props) {
		super(props);

		const {
			user,
			user: {
				Organization: { PayCodesEnabled },
			},
		} = this.props;

		this.state = {
			manualInvoices: [],
			selectedInvoiceNumber: "",
			minimumPayment: "",
			maximumPayment: "",
			showSearchOfficerModal: false,
			paycodesLoaded: false,
			startDepositDateRange: "",
			endDepositDateRange: "",
			reportsLoaded: false,
		};
		if (!user.isSuperAdmin()) {
			this.loadReports(user, PayCodesEnabled);
		}
		this.loadJurisdictions = debounce(utils.loadJurisdictions, 500);
	}

	loadReports = async (user, agencyId) => {
		const { reportsLoaded } = this.state;
        const orgId = agencyId <= 1 ? user.Organization.Id : agencyId 
		const ReportTypeArray = await user.getReports(orgId);
		let reportTypes = [];

		if (
			ReportTypeArray.length !== 0 &&
			Object.prototype.hasOwnProperty.call(ReportTypeArray[0], "Source")
		) {
			reportTypes = ReportTypeArray.map(reportType => ({
				value: reportType.Key,
				label: reportType.Name,
				source: reportType.Source,
				id: reportType.Id,
			})).filter(r => r.source.toLowerCase() !== "domo");
		} else {
			reportTypes = ReportTypeArray.map(reportType => ({
				value: reportType.type,
				label: reportType.name,
			}));
		}
		if (!user.isSuperAdmin() && !user.Organization.NonRKPayInvoicingEnabled) {
			reportTypes = reportTypes.filter(
				({ value }) => !manualInvoiceReportTypes.includes(value),
			);
		}

		if (!this.PayCodesEnabled) {
			reportTypes = reportTypes.filter(({ value }) => value !== "payroll_report");
		}

		this.reportTypes = reportTypes;
		if (!reportsLoaded) {
			// This is to help ensure the ReportList is loaded before the page
			setTimeout(() => {
				this.setState({ reportsLoaded: true });
			}, 500);
		}
	};

	createSelectBox = parameters => {
		const { updateFilters, setReportState, resetReportParams } = this.props;

		return {
			property: parameters.property,
			type: parameters.type ? parameters.type : "select",
			name: parameters.name,
			value: parameters.value,
			data: parameters.data,
			loading: parameters.loading,
			onChange: (e, property) => {
				const value = e ? e.value : "";
				updateFilters(property, value);
				setReportState({ renderReport: false });
				resetReportParams();

				if (parameters.callback) parameters.callback(value);
			},
			inactiveOption: !!parameters.onIncludeInactiveChange,
			includeInactiveDisabled: parameters.includeInactiveDisabled,
			onIncludeInactiveChange: parameters.onIncludeInactiveChange,
		};
	};

	buildAgencyFilter = () => {
		const { lang } = global;
		const {
			filters: { agencyId },
			agencies,
			loaders,
			setReportState,
			getClients,
			user,
		} = this.props;

		return this.createSelectBox({
			property: "agencyId",
			name: lang.t("Agency"),
			value: agencyId,
			data: agencies,
			loading: loaders.agencies,
			callback: value => {
				// Reset related filters
				setReportState(prevState => ({
					clients: [],
					projects: [],
					locations: [],
					contacts: [],
					filters: {
						...prevState.filters,
						clientId: "",
						projectId: "",
						locationId: "",
						clientSupervisorContactId: "",
					},
				}));
				if (value) getClients(value);
				if (value && user.isSuperAdmin()) {
					this.loadReports(user, value);
				}
			},
		});
	};

	buildClientFilter = (propagation = false) => {
		const { lang } = global;
		const {
			filters: { agencyId, clientId },
			clients,
			loaders,
			setReportState,
			getClients,
			getProjects,
			getContacts,
			user,
		} = this.props;

		return this.createSelectBox({
			property: "clientId",
			name: lang.t("Client"),
			value: clientId,
			data: clients,
			loading: loaders.clients,
			includeInactiveDisabled: user.isSuperAdmin() && agencyId === "",
			callback: value => {
				const { reportType } = this.props;

				// Reset related filters
				setReportState(prevState => ({
					projects: [],
					locations: [],
					contacts: [],
					filters: {
						...prevState.filters,
						projectId: "",
						locationId: "",
						clientSupervisorContactId: "",
					},
				}));
				if (value) getContacts(value);

				if (propagation && value) getProjects(value);

				if (manualInvoiceReportFilters.includes(reportType)) {
					this.getManualInvoices(agencyId, value || "");
				}
			},
			onIncludeInactiveChange: value => {
				setReportState(prevState => ({ filters: { ...prevState.filters, clientId: "" } }));

				// Reload clients changing the includeInactive flag
				if (!user.isSuperAdmin() || agencyId !== "") getClients(agencyId, value);
			},
		});
	};

	getManualInvoices = (organizationId, clientId) => {
		const { user } = this.props;
		if (user.isSuperAdmin() || user.Organization.NonRKPayInvoicingEnabled) {
			let queryParams = {
				clientId,
				organizationId,
				pageNumber: 1,
				displayCount: 100,
			};
			queryParams = Object.fromEntries(
				Object.entries(queryParams).filter(([, value]) => value),
			);
			PaymentService.getNonRKPayInvoices(undefined, queryParams)
				.then(({ data: { ItemList } }) =>
					this.setState({
						manualInvoices: ItemList.map(({ InvoiceNumber }) => ({
							value: InvoiceNumber,
							label: InvoiceNumber,
						})),
					}),
				)
				.catch(() => {});
		}
	};

	handleManualInvoiceSelection = e => {
		const selectedInvoiceNumber = e?.value || "";
		this.setState({ selectedInvoiceNumber });
	};

	buildOfficerFilter = () => {
		const { lang } = global;
		const {
			filters: {
				officer: { id },
			},
		} = this.props;

		return this.createSelectBox({
			property: "officer.id",
			name: lang.t("Search Officer"),
			value: id,
			type: "search_officer",
		});
	};

	buildClientSupervisorFilter = () => {
		const { lang } = global;
		const {
			filters: { clientSupervisorContactId },
			contacts,
			loaders,
		} = this.props;

		return this.createSelectBox({
			property: "clientSupervisorContactId",
			name: lang.t("Client Supervisor"),
			value: clientSupervisorContactId,
			data: contacts,
			loading: loaders.contacts,
		});
	};

	buildPayCodesFilter = () => {
		const { lang } = global;
		const { paycodesLoaded } = this.state;
		const {
			filters: { payCodeName },
			paycodes,
			loaders,
			getPaycodes,
		} = this.props;

		if (!paycodesLoaded) {
			getPaycodes();
			this.setState({ paycodesLoaded: true });
		}

		return this.createSelectBox({
			property: "payCodeName",
			name: lang.t("Pay Code"),
			value: payCodeName,
			data: paycodes,
			loading: loaders.paycodes,
		});
	};

	buildProjectFilter = (propagation = false) => {
		const { lang } = global;
		const {
			filters: { clientId, projectId },
			projects,
			loaders,
			setReportState,
			getProjects,
			getLocations,
		} = this.props;

		return this.createSelectBox({
			property: "projectId",
			name: lang.t("Project"),
			value: projectId,
			data: projects,
			loading: loaders.projects,
			includeInactiveDisabled: clientId === "",
			callback: value => {
				// Reset related filters
				setReportState(prevState => ({
					locations: [],
					filters: { ...prevState.filters, locationId: "" },
				}));

				if (propagation && value) getLocations(value);
			},
			onIncludeInactiveChange: value => {
				setReportState(prevState => ({ filters: { ...prevState.filters, projectId: "" } }));

				// Reload projects changing the includeInactive flag
				if (clientId !== "") getProjects(clientId, value);
			},
		});
	};

	buildLocationsFilter = () => {
		const { lang } = global;
		const {
			filters: { locationId },
			locations,
			loaders,
		} = this.props;

		return this.createSelectBox({
			property: "locationId",
			name: lang.t("Location"),
			value: locationId,
			data: locations,
			loading: loaders.locations,
		});
	};

	buildInvoiceStatusFilter = () => {
		const { lang } = global;
		const {
			filters: { invoiceStatus },
		} = this.props;

		return this.createSelectBox({
			property: "invoiceStatus",
			name: lang.t("Invoice Status"),
			value: invoiceStatus,
			data: INOVICE_STATUSES_SELECT,
			loading: false,
		});
	};

	build_client_overview_selectors = () => {
		const { user } = this.props;
		const filters = [];

		if (user.isSuperAdmin()) {
			filters.push(this.buildClientFilter());
		} else if (!user.isClient()) filters.push(this.buildClientFilter());

		filters.push(this.buildClientSupervisorFilter());

		return filters;
	};

	build_payroll_report_selectors = () => {
		const filters = [];

		filters.push(this.buildPayCodesFilter());

		return filters;
	};

	build_project_overview_selectors = () => {
		const { user } = this.props;
		const filters = [];

		if (user.isSuperAdmin()) {
			filters.push(this.buildClientFilter(true));
		} else if (!user.isClient()) filters.push(this.buildClientFilter(true));

		filters.push(this.buildClientSupervisorFilter());
		filters.push(this.buildProjectFilter());

		return filters;
	};

	build_officer_overview_selectors = () => [this.buildOfficerFilter()];

	build_location_overview_selectors = () => {
		const { user } = this.props;
		const filters = [];

		if (user.isSuperAdmin()) {
			filters.push(this.buildClientFilter(true));
		} else if (!user.isClient()) filters.push(this.buildClientFilter(true));

		filters.push(this.buildClientSupervisorFilter());
		filters.push(this.buildProjectFilter(true));
		filters.push(this.buildLocationsFilter());

		return filters;
	};

	build_jurisdiction_overview_selectors = () => {
		const { lang } = global;
		const {
			filters: { jurisdictionId },
			user,
		} = this.props;

		const filters = [];

		if (!user.isLEA()) {
			filters.push(
				this.createSelectBox({
					property: "jurisdictionId",
					name: lang.t("Search Jurisdictions"),
					value: jurisdictionId,
					type: "search_jurisdiction",
				}),
			);
		}

		return filters;
	};

	build_agency_selector = () => {
		const { user } = this.props;
		const filters = [];

		if (user.isSuperAdmin()) filters.push(this.buildAgencyFilter());

		return filters;
	};

	build_super_admin_agency_selectors = () => this.build_agency_selector();

	build_agency_overview_selectors = () => [];

	build_agency_overview_detailed_selectors = () => [];

	build_coordinator_overview_selectors = () => [];

	build_payment_history_selectors = () => {
		const filters = [];
		const { user } = this.props;

		filters.push(this.buildOfficerFilter());
		filters.push(this.buildClientFilter(true));
		// prevent showing the project filter for coordinator users
		if (!user.isCoordinator()) filters.push(this.buildProjectFilter(true));

		filters.push(this.buildLocationsFilter());

		return filters;
	};

	build_invoice_report_selectors = () => [
		...this.build_payment_history_selectors(),
		this.buildInvoiceStatusFilter(),
	];

	build_department_invoice_report_selectors = () => this.build_payment_history_selectors();

	build_fairness_selectors = () => {
		const {
			user,
			filters: { jurisdictionId },
		} = this.props;

		const filters = [];

		if (user.isSuperAdmin()) {
			filters.push(
				this.createSelectBox({
					property: "jurisdictionId",
					name: "Search Jurisdiction",
					value: jurisdictionId,
					type: "search_jurisdiction",
				}),
			);
		}

		return filters;
	};

	build_calloff_selectors = () => {
		const filters = [];
		return filters;
	};

	build_manual_invoice_selectors = () => {
		const filters = [];
		filters.push(this.buildClientFilter());

		return filters;
	};

	build_manual_aging_selectors = () => {
		const filters = [];
		filters.push(this.buildClientFilter());

		return filters;
	};

	// PBI-159: RKPay Invoice Aging report uses the same filters as manual invoice aging
	build_invoice_aging_selectors = () => this.build_manual_aging_selectors();

	build_applied_payments_selectors = () => {
		const filters = [];
		filters.push(this.buildClientFilter());

		return filters;
	};

	build_deposit_selectors = () => {
		const filters = [];

		filters.push(this.buildClientFilter());
		filters.push(this.buildOfficerFilter());

		return filters;
	};

	onReportChange = event => {
		const { setReportState, resetFilters, resetReportParams } = this.props;

		resetFilters(() =>
			setReportState({
				reportType: event?.value || "",
				reportId: event?.id || "",
				reportSource: event?.source || "rollkall",
				renderReport: false,
			}),
		);
		resetReportParams();

		this.setState({
			selectedInvoiceNumber: "",
			minimumPayment: "",
			maximumPayment: "",
			startDepositDateRange: "",
			endDepositDateRange: "",
		});
	};

	handleIncludeInactiveChange = (property, value) => {
		const { onIncludeInactiveChange } = this.props;
		onIncludeInactiveChange(property, value);
	};

	handleDateChange =
		({ target: { value } }) =>
		property => {
			const {
				reportType,
				updateFilters,
				filters: { agencyId, clientId },
			} = this.props;
			updateFilters(property, value);
			if (manualInvoiceReportTypes.includes(reportType) && reportType !== "deposit")
				this.getManualInvoices(agencyId, clientId);
		};

	handlePaymentChange = ({ target: { name, value } }) => {
		this.setState({ [name]: value.replace(/\D/g, "") });
	};

	handleDepositDateChange =
		({ target: { value } }) =>
		property => {
			const { updateFilters } = this.props;
			updateFilters(property, value);
			this.setState({ [property]: value });
		};

	handleManualInvoiceReport = () => {
		const {
			reportType,
			generateManualInvoiceCallback,
			filters: { agencyId: organizationId, clientId, startDateRange, endDateRange, officer },
		} = this.props;
		const {
			selectedInvoiceNumber: invoiceNumber,
			minimumPayment,
			maximumPayment,
			startDepositDateRange,
			endDepositDateRange,
		} = this.state;
		const startDate = startDateRange;
		const endDate = endDateRange;
		let queryParams = {};
		switch (reportType) {
			case "manual_invoice":
				queryParams = {
					invoiceNumber,
					clientId,
					organizationId,
					issueStartDate: startDate,
					issueEndDate: endDate,
				};
				break;
			case "manual_aging":
				queryParams = { organizationId, clientId };
				break;
			case "applied_payments":
				queryParams = {
					invoiceNumber,
					clientId,
					organizationId,
					minAmount: minimumPayment,
					maxAmount: maximumPayment,
					paymentStartDate: startDate,
					paymentEndDate: endDate,
				};
				break;

			case "deposit":
				queryParams = {
					clientId,
					startDepositDateRange,
					endDepositDateRange,
					officerId: officer.id,
					agencyOrganizationId: organizationId,
					startJobDateRange: startDate,
					endJobDateRange: endDate,
				};
				break;
			default:
				break;
		}
		// Filter any empty/undefined params from the object
		queryParams = Object.fromEntries(Object.entries(queryParams).filter(([, value]) => value));
		generateManualInvoiceCallback(reportType, queryParams);
	};

	areDatesValid = () => {
		const {
			filters: { startDateRange, endDateRange },
		} = this.props;

		// If either date is blank, they are not valid
		if (startDateRange === "" || endDateRange === "") {
			return false;
		}
		// By default, we will test both dates against a yyyy-mm-dd format
		let dateRegex = /(\d{4})-(\d{2})-(\d{2})/;
		if (utils.isDesktopSafari || utils.isIE) {
			// Make sure the due date is after the issue date
			const selectedStart = new Date(`${startDateRange} 00:00`);
			const selectedEnd = new Date(`${endDateRange} 00:00`);
			if (selectedStart.getTime() > selectedEnd.getTime()) {
				return false;
			}
			// If all previous checks are met on Safari/IE, we should test against a mm/dd/yyyy format
			dateRegex = /(\d{2})\/(\d{2})\/(\d{4})/;
		} else {
			// Verify that a date selected without using the datepicker is not before the invoice issue date
			const selectedStart = new Date(`${startDateRange}T00:00:00`);
			const selectedEnd = new Date(`${endDateRange}T00:00:00`);
			if (selectedStart.getTime() > selectedEnd.getTime()) {
				return false;
			}
		}
		return dateRegex.test(startDateRange) && dateRegex.test(endDateRange);
	};

	clearOfficerInput = () => {
		const { updateFilters } = this.props;
		updateFilters("officer", { id: "", name: "" });
	};

	handleOfficerSelection = officer => {
		const { updateFilters, setReportState, resetReportParams } = this.props;
		const { Id: id, FirstName, LastName } = officer;
		updateFilters("officer", { id, name: `${FirstName} ${LastName}` });
		setReportState({ renderReport: false });
		resetReportParams();
	};

	render() {
		const { lang } = global;
		const {
			manualInvoices,
			selectedInvoiceNumber,
			minimumPayment,
			maximumPayment,
			showSearchOfficerModal,
			startDepositDateRange,
			endDepositDateRange,
		} = this.state;
		const { user, reportType, reportSource, filters, generateReportBtnCallback, resetFilters } =
			this.props;

		const manualInvoiceFilter = {
			name: "Invoice Number",
			value: selectedInvoiceNumber,
			data: manualInvoices,
			isClearable: true,
		};
		const agencySelectBox = user.isSuperAdmin()
			? this.build_super_admin_agency_selectors()
			: [];
		const selectBoxes =
			reportType !== "" && reportSource === "rollkall"
				? this[`build_${reportType}_selectors`]()
				: [];
		const startDateObject = new Date(new Date(`${filters.startDateRange}T00:00:00`));
		const minEndDate = utils.formatDate(
			new Date(
				new Date(new Date(`${filters.startDateRange}T00:00:00`)).setDate(
					startDateObject.getDate(),
				),
			),
			undefined,
			undefined,
			"noslash",
		);
		const startDepositDateObject = new Date(new Date(`${startDepositDateRange}T00:00:00`));
		const minDepositEndDate = utils.formatDate(
			new Date(
				new Date(new Date(`${startDepositDateRange}T00:00:00`)).setDate(
					startDepositDateObject.getDate() + 1,
				),
			),
			undefined,
			undefined,
			"noslash",
		);

		return (
			<Fragment>
				<div className="flex flex-align-items-center flex-justify-content-space-between margin-bottom-20">
					<div className="font-size-16 font-bold ">{lang.t("Filter")}</div>
					<Button
						id="clear-report-filters"
						bsStyle="link"
						onClick={() => resetFilters()}
						className="font-size-12 color-blue-picton"
					>
						{lang.t("Clear")}
					</Button>
				</div>
				<For
					each="item"
					index="index"
					of={agencySelectBox}
				>
					<div
						key={`reports_filter_${index}`}
						className={`margin-bottom-25 ${item.type === "search_officer" ? "form-group" : ""}`}
					>
						<SelectBox
							filter={item}
							handleOnFilter={item.onChange}
							handleIncludeInactiveChange={this.handleIncludeInactiveChange}
						/>
					</div>
				</For>
				<FormGroup className="margin-bottom-25">
					<label htmlFor="reportType">{lang.t("Report Type")}</label>
					<ReactSelect
						value={reportType}
						name="reportType"
						onChange={this.onReportChange}
						options={this.reportTypes}
						isClearable
					/>
				</FormGroup>
				<For
					each="item"
					index="index"
					of={selectBoxes}
				>
					<div
						key={`reports_filter_${index}`}
						className={`margin-bottom-25 ${item.type === "search_officer" ? "form-group" : ""}`}
					>
						<Choose>
							<When condition={item.type === "search_officer"}>
								<div className="input-group search-officer">
									<input
										className="form-control"
										placeholder={lang.t("Search for Officer")}
										value={filters.officer.name}
										onClick={() =>
											this.setState({ showSearchOfficerModal: true })
										}
										onChange={() => {}}
									/>
									<span className="input-group-btn">
										<button
											type="button"
											aria-label="Search Officers"
											className="btn btn-primary"
											onClick={() => {
												if (filters.officer.id === "")
													this.setState({ showSearchOfficerModal: true });
												else this.clearOfficerInput();
											}}
										>
											<Choose>
												<When condition={filters.officer.id === ""}>
													<i className="glyphicon glyphicon-search" />
												</When>
												<Otherwise>
													<i className="glyphicon glyphicon-remove" />
												</Otherwise>
											</Choose>
										</button>
									</span>
								</div>
								<SearchOfficerModal
									show={showSearchOfficerModal}
									user={user}
									closeModal={() =>
										this.setState({ showSearchOfficerModal: false })
									}
									/* Callback function for SearchOfficerModal returns the selected officer */
									callback={this.handleOfficerSelection}
								/>
							</When>
							<When condition={item.type === "search_jurisdiction"}>
								<FormGroup>
									<ControlLabel>{item.name}</ControlLabel>
									<AsyncSelect
										placeholder="Search Jurisdiction"
										name="jurisdictionId"
										cacheOptions
										loadOptions={this.loadJurisdictions}
										onChange={e => item.onChange(e, "jurisdictionId")}
										isClearable
									/>
								</FormGroup>
							</When>
							<Otherwise>
								<SelectBox
									filter={item}
									handleOnFilter={item.onChange}
									handleIncludeInactiveChange={this.handleIncludeInactiveChange}
								/>
							</Otherwise>
						</Choose>
					</div>
				</For>
				<If condition={manualInvoiceReportFilters.includes(reportType)}>
					<SelectBox
						filter={manualInvoiceFilter}
						handleOnFilter={this.handleManualInvoiceSelection}
						disabled={filters.clientId === ""}
					/>
				</If>
				<If condition={reportType === "applied_payments"}>
					<div className="flex flex-direction-column">
						<div className="flex-1">
							<div className="form-group">
								<label
									className="display-block"
									htmlFor="minPaymentAmt"
								>
									Minimum Payment Amount
									<input
										type="tel"
										name="minimumPayment"
										id="minPaymentAmt"
										className="form-control"
										value={minimumPayment}
										onChange={this.handlePaymentChange}
									/>
								</label>
							</div>
						</div>
						<div className="flex-1">
							<div className="form-group">
								<label
									className="display-block"
									htmlFor="maxPaymentAmt"
								>
									Maximum Payment Amount
									<input
										type="tel"
										name="maximumPayment"
										id="maxPaymentAmt"
										className="form-control"
										value={maximumPayment}
										onChange={this.handlePaymentChange}
									/>
								</label>
							</div>
						</div>
					</div>
				</If>
				<If condition={reportType !== ""}>
					<If condition={reportSource === "rollkall"}>
						<If
							condition={
								reportType !== "manual_aging" && reportType !== "invoice_aging"
							}
						>
							<div className="flex flex-direction-column margin-bottom-25">
								<div className="flex-1">
									<FormGroup>
										<label htmlFor="startDateRange">
											<Choose>
												<When condition={reportType === "deposit"}>
													{lang.t("Job Start Date")}
												</When>
												<When
													condition={manualInvoiceReportTypes.includes(
														reportType,
													)}
												>
													<If condition={reportType === "manual_invoice"}>
														{lang.t("Invoice Issued")}
														<br />
														<br />
													</If>
													{lang.t("From")}
												</When>
												<Otherwise>{lang.t("Start Date")}</Otherwise>
											</Choose>
										</label>
										<Choose>
											<When condition={utils.isDesktopSafari || utils.isIE}>
												<MaskedInput
													name="startDateRange"
													id="startDateRange"
													type="text"
													mask={dateMask}
													className="form-control"
													placeholder="mm/dd/yyyy"
													value={filters.startDateRange}
													onChange={e =>
														this.handleDateChange(e)("startDateRange")
													}
												/>
											</When>
											<Otherwise>
												<input
													name="jobDate"
													id="jobDate"
													className="form-control"
													type="date"
													max={
														filters.endDateRange
															? filters.endDateRange
															: undefined
													}
													value={filters.startDateRange}
													onChange={e =>
														this.handleDateChange(e)("startDateRange")
													}
												/>
											</Otherwise>
										</Choose>
									</FormGroup>
								</div>
								<div className="flex-1">
									<FormGroup>
										<label htmlFor="endDateRange">
											<Choose>
												<When condition={reportType === "deposit"}>
													{lang.t("Job End Date")}
												</When>
												<When
													condition={manualInvoiceReportTypes.includes(
														reportType,
													)}
												>
													{lang.t("To")}
												</When>
												<Otherwise>{lang.t("End Date")}</Otherwise>
											</Choose>
										</label>
										<Choose>
											<When condition={utils.isDesktopSafari || utils.isIE}>
												<MaskedInput
													name="endDateRange"
													id="endDateRange"
													type="text"
													mask={dateMask}
													className="form-control"
													placeholder="mm/dd/yyyy"
													value={filters.endDateRange}
													onChange={e =>
														this.handleDateChange(e)("endDateRange")
													}
												/>
											</When>
											<Otherwise>
												<input
													name="jobDate"
													id="jobDate"
													className="form-control"
													type="date"
													min={minEndDate}
													value={filters.endDateRange}
													onChange={e =>
														this.handleDateChange(e)("endDateRange")
													}
												/>
											</Otherwise>
										</Choose>
									</FormGroup>
								</div>
							</div>
							<div className="flex flex-direction-column margin-bottom-25">
								<If condition={reportType === "deposit"}>
									<div className="flex-1">
										<FormGroup>
											<label htmlFor="startDepositDateRange">
												{lang.t("Deposit Start Date")}
											</label>
											<Choose>
												<When
													condition={utils.isDesktopSafari || utils.isIE}
												>
													<MaskedInput
														name="startDepositDateRange"
														id="startDepositDateRange"
														type="text"
														mask={dateMask}
														className="form-control"
														placeholder="mm/dd/yyyy"
														value={startDepositDateRange}
														onChange={e =>
															this.handleDepositDateChange(e)(
																"startDepositDateRange",
															)
														}
													/>
												</When>
												<Otherwise>
													<input
														name="startDepositDateRange"
														id="startDepositDateRange"
														className="form-control"
														type="date"
														max={maxStartDate}
														value={startDepositDateRange}
														onChange={e =>
															this.handleDepositDateChange(e)(
																"startDepositDateRange",
															)
														}
													/>
												</Otherwise>
											</Choose>
										</FormGroup>
									</div>
									<div className="flex-1">
										<FormGroup>
											<label htmlFor="endDepositDateRange">
												{lang.t("Deposit End Date")}
											</label>
											<Choose>
												<When
													condition={utils.isDesktopSafari || utils.isIE}
												>
													<MaskedInput
														name="endDepositDateRange"
														id="endDepositDateRange"
														type="text"
														mask={dateMask}
														className="form-control"
														placeholder="mm/dd/yyyy"
														value={endDepositDateRange}
														onChange={e =>
															this.handleDepositDateChange(e)(
																"endDepositDateRange",
															)
														}
													/>
												</When>
												<Otherwise>
													<input
														name="endDepositDateRange"
														id="endDepositDateRange"
														className="form-control"
														type="date"
														min={minDepositEndDate}
														value={endDepositDateRange}
														onChange={e =>
															this.handleDepositDateChange(e)(
																"endDepositDateRange",
															)
														}
													/>
												</Otherwise>
											</Choose>
										</FormGroup>
									</div>
								</If>
							</div>
						</If>
					</If>
					<Button
						bsStyle="primary"
						className="form-control"
						onClick={
							manualInvoiceReportTypes.includes(reportType)
								? this.handleManualInvoiceReport
								: generateReportBtnCallback
						}
						disabled={!this.areDatesValid() && reportType !== "manual_aging"}
					>
						{lang.t("Generate Report")}
					</Button>
				</If>
			</Fragment>
		);
	}
}

SideBar.propTypes = {
	agencies: PropTypes.array,
	clients: PropTypes.array,
	contacts: PropTypes.array,
	paycodes: PropTypes.array,
	filters: PropTypes.object,
	generateReportBtnCallback: PropTypes.func.isRequired,
	generateManualInvoiceCallback: PropTypes.func.isRequired,
	getClients: PropTypes.func.isRequired,
	getContacts: PropTypes.func.isRequired,
	getLocations: PropTypes.func.isRequired,
	getProjects: PropTypes.func.isRequired,
	getPaycodes: PropTypes.func.isRequired,
	loaders: PropTypes.object.isRequired,
	locations: PropTypes.array,
	onIncludeInactiveChange: PropTypes.func,
	projects: PropTypes.array,
	setReportState: PropTypes.func.isRequired,
	reportType: PropTypes.string,
	reportSource: PropTypes.string,
	resetFilters: PropTypes.func.isRequired,
	resetReportParams: PropTypes.func.isRequired,
	updateFilters: PropTypes.func.isRequired,
	user: PropTypes.object,
	PayCodesEnabled: PropTypes.string,
};

SideBar.defaultProps = {
	agencies: [],
	clients: [],
	contacts: [],
	paycodes: [],
	filters: {},
	locations: [],
	projects: [],
	reportType: "",
	reportSource: "",
	user: {},
	PayCodesEnabled: "",
	onIncludeInactiveChange: () => undefined,
};
export default SideBar;
